// Agenda do aluno — lista cronológica das aulas (próximas + passadas
// recentes), agrupada por dia. Filtro de matéria pra esconder o que não
// quer ver. Read-only (aluno não marca/cancela).

import { signal, computed } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  BookOpen,
  CalendarCheck,
  CalendarClock,
  CalendarX,
  ChevronLeft,
  ChevronRight,
  Clock,
  Filter,
  LayoutGrid,
  List as ListIcon,
  Loader2,
  RefreshCw,
  Repeat,
  User,
  X
} from "lucide-react";
import { api, ApiError, type AulaStudentRow } from "./api";
import { AnimatedTabs } from "@app/ui/components/animated-tabs";

const phase = signal<"loading" | "ready" | "error">("loading");
const errMsg = signal<string | null>(null);
const items = signal<AulaStudentRow[]>([]);
const hiddenDisciplinas = signal<Set<string>>(new Set());

export const upcomingAulas = computed(() => {
  const now = Date.now();
  return items.value
    .filter(
      (a) =>
        a.status === "scheduled" &&
        new Date(a.dataHora).getTime() >= now &&
        !hiddenDisciplinas.value.has(a.disciplinaId)
    )
    .slice(0, 5);
});

async function load() {
  phase.value = "loading";
  errMsg.value = null;
  try {
    const from = new Date(Date.now() - 14 * 86_400_000).toISOString();
    const to = new Date(Date.now() + 90 * 86_400_000).toISOString();
    const r = await api.listMyAulas({ from, to });
    items.value = r.aulas;
    phase.value = "ready";
  } catch (e) {
    if (e instanceof ApiError && (e.status === 401 || e.status === 403)) {
      window.location.href = "/auth/";
      return;
    }
    errMsg.value = e instanceof Error ? e.message : String(e);
    phase.value = "error";
  }
}

/** Carrega agenda em background — chamado no boot pra alimentar
 *  upcomingAulas (próximos eventos na home). */
export function startAgendaPreload() {
  void load();
}

function fmtDay(iso: string): string {
  return new Date(iso).toLocaleDateString("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "long"
  });
}

function fmtTime(iso: string): string {
  return new Date(iso).toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit"
  });
}

function isToday(iso: string): boolean {
  const t = new Date(iso);
  const today = new Date();
  return (
    t.getFullYear() === today.getFullYear() &&
    t.getMonth() === today.getMonth() &&
    t.getDate() === today.getDate()
  );
}

function dayKey(iso: string): string {
  const t = new Date(iso);
  return `${t.getFullYear()}-${String(t.getMonth() + 1).padStart(2, "0")}-${String(t.getDate()).padStart(2, "0")}`;
}

export function AgendaView() {
  useEffect(() => {
    void load();
  }, []);

  const [openAula, setOpenAula] = useState<AulaStudentRow | null>(null);
  const [viewMode, setViewMode] = useState<"list" | "calendar">(() => {
    try {
      return (localStorage.getItem("agenda-view-mode") as any) || "list";
    } catch {
      return "list";
    }
  });
  function changeViewMode(m: "list" | "calendar") {
    setViewMode(m);
    try {
      localStorage.setItem("agenda-view-mode", m);
    } catch {
      /* ignore */
    }
  }
  const [calendarMonth, setCalendarMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });
  const list = items.value;
  const allDisciplinas = Array.from(
    new Map(list.map((a) => [a.disciplinaId, a.disciplinaName])).entries()
  );
  const visible = list.filter(
    (a) => !hiddenDisciplinas.value.has(a.disciplinaId)
  );

  // Agrupa por dia
  const byDay = new Map<string, AulaStudentRow[]>();
  for (const a of visible) {
    const k = dayKey(a.dataHora);
    if (!byDay.has(k)) byDay.set(k, []);
    byDay.get(k)!.push(a);
  }
  const dayKeys = Array.from(byDay.keys()).sort();

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <CalendarClock className="size-6 text-primary" />
            Agenda
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Suas aulas — próximas e recentes. O professor confirma quando ela
            acontece (descontando 1 crédito).
          </p>
        </div>
        <div class="flex items-center gap-2">
          <AnimatedTabs<"list" | "calendar">
            ariaLabel="Modo de visualização"
            size="sm"
            items={[
              { id: "list", label: "Lista", icon: ListIcon, title: "Lista" },
              { id: "calendar", label: "Calendário", icon: LayoutGrid, title: "Calendário" }
            ]}
            value={viewMode}
            onChange={(v) => changeViewMode(v)}
          />
          <button
            type="button"
            onClick={load}
            title="Atualizar"
            aria-label="Atualizar"
            class="grid size-8 place-items-center rounded-md border border-border bg-background text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <RefreshCw className="size-3.5" />
          </button>
        </div>
      </header>

      {allDisciplinas.length > 1 && (
        <section class="rounded-md border border-border bg-card/40 p-3">
          <div class="mb-2 flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
            <Filter className="size-3.5" />
            Filtrar matérias
          </div>
          <div class="flex flex-wrap gap-1.5">
            {allDisciplinas.map(([id, name]) => {
              const hidden = hiddenDisciplinas.value.has(id);
              return (
                <button
                  key={id}
                  type="button"
                  onClick={() => {
                    const next = new Set(hiddenDisciplinas.value);
                    if (hidden) next.delete(id);
                    else next.add(id);
                    hiddenDisciplinas.value = next;
                  }}
                  class={`rounded-md border px-3 py-1 text-[11px] font-medium transition-colors ${
                    hidden
                      ? "border-border bg-background text-muted-foreground line-through opacity-60"
                      : "border-primary/40 bg-primary/10 text-primary"
                  }`}
                >
                  {name}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {phase.value === "loading" && (
        <p class="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> Carregando…
        </p>
      )}

      {phase.value === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 p-4">
          <p class="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            {errMsg.value}
          </p>
        </div>
      )}

      {phase.value === "ready" && dayKeys.length === 0 && (
        <div class="rounded-md border border-dashed border-border bg-card/40 p-10 text-center">
          <CalendarClock className="mx-auto mb-3 size-8 text-muted-foreground/60" />
          <p class="text-sm text-muted-foreground">
            Nenhuma aula no período.
          </p>
        </div>
      )}

      {viewMode === "calendar" && phase.value === "ready" && (
        <CalendarMonth
          month={calendarMonth}
          aulas={visible}
          onPrev={() =>
            setCalendarMonth(
              new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() - 1, 1)
            )
          }
          onNext={() =>
            setCalendarMonth(
              new Date(calendarMonth.getFullYear(), calendarMonth.getMonth() + 1, 1)
            )
          }
          onToday={() => {
            const d = new Date();
            setCalendarMonth(new Date(d.getFullYear(), d.getMonth(), 1));
          }}
          onOpenAula={(a) => setOpenAula(a)}
        />
      )}

      <div class={`space-y-5 ${viewMode === "calendar" ? "hidden" : ""}`}>
        {dayKeys.map((k) => {
          const dayItems = byDay.get(k)!;
          const sampleIso = dayItems[0].dataHora;
          const today = isToday(sampleIso);
          const tmr = new Date();
          tmr.setDate(tmr.getDate() + 1);
          const tomorrow =
            new Date(sampleIso).getFullYear() === tmr.getFullYear() &&
            new Date(sampleIso).getMonth() === tmr.getMonth() &&
            new Date(sampleIso).getDate() === tmr.getDate();
          const d = new Date(sampleIso);
          const dayNum = d.getDate();
          const weekday = d
            .toLocaleDateString("pt-BR", { weekday: "long" })
            .replace(",", "");
          const month = d.toLocaleDateString("pt-BR", { month: "long" });
          return (
            <section key={k}>
              <h2
                class={`mb-3 flex items-baseline gap-3 ${
                  today ? "text-primary" : ""
                }`}
              >
                <span
                  class={`text-4xl font-extrabold leading-none tabular-nums ${
                    today
                      ? "text-primary"
                      : tomorrow
                      ? "text-emerald-700 dark:text-emerald-400"
                      : "text-foreground"
                  }`}
                >
                  {dayNum}
                </span>
                <div class="flex flex-col leading-tight">
                  <span class="flex items-center gap-2 text-sm font-bold capitalize">
                    {weekday}
                    {today && (
                      <span class="rounded bg-primary/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary ring-1 ring-primary/25">
                        Hoje
                      </span>
                    )}
                    {tomorrow && (
                      <span class="rounded bg-emerald-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 ring-1 ring-emerald-500/25 dark:text-emerald-400">
                        Amanhã
                      </span>
                    )}
                  </span>
                  <span class="text-xs capitalize text-muted-foreground">
                    {month} · {dayItems.length}{" "}
                    {dayItems.length === 1 ? "aula" : "aulas"}
                  </span>
                </div>
              </h2>
              <ul class="space-y-2">
                {dayItems.map((a) => (
                  <AulaItem
                    key={a.id}
                    a={a}
                    isToday={today}
                    isTomorrow={tomorrow}
                    onOpen={() => setOpenAula(a)}
                  />
                ))}
              </ul>
            </section>
          );
        })}
      </div>

      {openAula && (
        <AulaModal a={openAula} onClose={() => setOpenAula(null)} />
      )}
    </div>
  );
}

function AulaItem({
  a,
  isToday: isTodayItem,
  isTomorrow,
  onOpen
}: {
  a: AulaStudentRow;
  isToday?: boolean;
  isTomorrow?: boolean;
  onOpen: () => void;
}) {
  const isPast = new Date(a.dataHora).getTime() < Date.now();
  const isRealized = a.status === "realized";
  const isCanceled = a.status === "canceled";
  // Cor do bloco de hora reflete o estado da aula
  let timeBlockClass: string;
  let topLabel: string | null = null;
  if (isCanceled) {
    timeBlockClass = "bg-muted text-muted-foreground ring-border";
  } else if (isRealized) {
    timeBlockClass =
      "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 dark:text-emerald-400";
    topLabel = "DADA";
  } else if (isPast) {
    timeBlockClass =
      "bg-amber-500/15 text-amber-700 ring-amber-500/30 dark:text-amber-400";
  } else if (isTodayItem) {
    timeBlockClass = "bg-primary/20 text-primary ring-primary/40";
    topLabel = "HOJE";
  } else if (isTomorrow) {
    timeBlockClass =
      "bg-emerald-500/15 text-emerald-700 ring-emerald-500/30 dark:text-emerald-400";
    topLabel = "AMANHÃ";
  } else {
    timeBlockClass = "bg-primary/10 text-primary ring-primary/25";
  }
  return (
    <li
      class={`flex flex-wrap items-center gap-4 rounded-md border px-4 py-3 cursor-pointer transition-colors hover:bg-accent/30 ${
        isCanceled
          ? "border-border bg-muted/30 opacity-60"
          : isRealized
          ? "border-emerald-500/30 bg-emerald-500/5"
          : isPast
          ? "border-amber-500/30 bg-amber-500/5"
          : isTodayItem
          ? "border-primary/40 bg-primary/5"
          : "border-border bg-card/60"
      }`}
      onClick={onOpen}
    >
      <div
        class={`flex w-20 shrink-0 flex-col items-center justify-center rounded-md px-2 py-2 ring-1 ${timeBlockClass}`}
      >
        {topLabel && (
          <span class="text-[9px] font-bold leading-none tracking-wider">
            {topLabel}
          </span>
        )}
        <span
          class={`font-extrabold leading-none tabular-nums ${
            topLabel ? "mt-1 text-xl" : "text-2xl"
          }`}
        >
          {fmtTime(a.dataHora)}
        </span>
        <span class="mt-1 text-[9px] font-semibold leading-none opacity-75">
          {a.duracaoMin} MIN
        </span>
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex flex-wrap items-center gap-2">
          <span class="text-sm font-bold">{a.disciplinaName}</span>
          <StatusBadge status={a.status} />
        </div>
        <div class="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
          <User className="size-3" />
          <span>{a.professorName}</span>
          {a.recorrenciaGrupoId && (
            <>
              <span>·</span>
              <span class="italic">recorrente</span>
            </>
          )}
        </div>
        {a.descricao && (
          <p class="mt-1 text-[11px] text-muted-foreground">{a.descricao}</p>
        )}
        {isCanceled && a.canceledReason && (
          <p class="mt-1 text-[11px] italic text-muted-foreground">
            Motivo: {a.canceledReason}
          </p>
        )}
      </div>
    </li>
  );
}

export function AulaModal({
  a,
  onClose
}: {
  a: AulaStudentRow;
  onClose: () => void;
}) {
  const dt = new Date(a.dataHora);
  const weekday = dt.toLocaleDateString("pt-BR", { weekday: "long" });
  const dayMonth = dt.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric"
  });
  const time = dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  const dayNum = dt.getDate();
  return (
    <div
      class="fixed inset-0 z-50 grid place-items-center bg-background/80 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        class="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden rounded-md border border-border bg-card shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div class="flex items-start gap-4 min-w-0">
            <div class="flex w-20 shrink-0 flex-col items-center justify-center rounded-md bg-primary/15 px-2 py-2 text-primary ring-1 ring-primary/30">
              <span class="text-[9px] font-bold uppercase tracking-wider opacity-80">
                {weekday.replace(/-feira/, "").slice(0, 3).toUpperCase()}
              </span>
              <span class="text-3xl font-extrabold leading-none tabular-nums">
                {String(dayNum).padStart(2, "0")}
              </span>
              <span class="mt-0.5 text-[10px] font-semibold tabular-nums opacity-75">
                {time}
              </span>
            </div>
            <div class="min-w-0">
              <h2 class="text-base font-bold">{a.disciplinaName}</h2>
              <p class="mt-0.5 text-xs capitalize text-muted-foreground">
                {weekday}, {dayMonth}
              </p>
              <div class="mt-2">
                <StatusBadge status={a.status} />
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            title="Fechar"
            aria-label="Fechar"
            class="grid size-8 shrink-0 place-items-center rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            <X className="size-4" />
          </button>
        </header>
        <div class="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-sm">
          <DetailRow icon={Clock} label="Duração">
            {a.duracaoMin} minutos
          </DetailRow>
          <DetailRow icon={User} label="Professor">
            {a.professorName}
          </DetailRow>
          <DetailRow icon={BookOpen} label="Disciplina">
            {a.disciplinaName}
          </DetailRow>
          {a.recorrenciaGrupoId && (
            <DetailRow icon={Repeat} label="Recorrência">
              <span class="italic text-muted-foreground">
                Esta aula faz parte de uma série recorrente.
              </span>
            </DetailRow>
          )}
          {a.descricao && (
            <div>
              <div class="mb-1 text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
                Conteúdo / observações
              </div>
              <p class="whitespace-pre-wrap leading-relaxed">{a.descricao}</p>
            </div>
          )}
          {a.status === "canceled" && a.canceledReason && (
            <div class="rounded-md border border-destructive/20 bg-destructive/5 p-3">
              <div class="mb-1 text-[11px] font-bold uppercase tracking-wider text-destructive">
                Motivo do cancelamento
              </div>
              <p class="text-xs">{a.canceledReason}</p>
            </div>
          )}
          {a.status === "realized" && a.realizedAt && (
            <p class="text-[11px] italic text-muted-foreground">
              Marcada como dada em{" "}
              {new Date(a.realizedAt).toLocaleString("pt-BR")}
            </p>
          )}
        </div>
        <footer class="flex items-center justify-end gap-2 border-t border-border bg-muted/20 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-xs font-semibold text-primary-foreground hover:bg-primary/90"
          >
            Fechar
          </button>
        </footer>
      </div>
    </div>
  );
}

function DetailRow({
  icon: Icon,
  label,
  children
}: {
  icon: typeof Clock;
  label: string;
  children: any;
}) {
  return (
    <div class="flex items-start gap-3">
      <Icon className="mt-0.5 size-4 shrink-0 text-muted-foreground" />
      <div class="min-w-0 flex-1">
        <div class="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
        <div class="text-sm">{children}</div>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: AulaStudentRow["status"] }) {
  const map = {
    scheduled: { label: "Agendada", cls: "bg-blue-500/15 text-blue-700 dark:text-blue-400", Icon: CalendarClock },
    realized: { label: "Dada", cls: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400", Icon: CalendarCheck },
    canceled: { label: "Cancelada", cls: "bg-muted text-muted-foreground", Icon: CalendarX },
    no_show: { label: "Falta", cls: "bg-amber-500/15 text-amber-700 dark:text-amber-400", Icon: CalendarX }
  } as const;
  const m = map[status];
  const Icon = m.Icon;
  return (
    <span class={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-bold ${m.cls}`}>
      <Icon className="size-3" />
      {m.label}
    </span>
  );
}

function CalendarMonth({
  month,
  aulas,
  onPrev,
  onNext,
  onToday,
  onOpenAula
}: {
  month: Date;
  aulas: AulaStudentRow[];
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onOpenAula: (a: AulaStudentRow) => void;
}) {
  const year = month.getFullYear();
  const monthIdx = month.getMonth();
  const firstDay = new Date(year, monthIdx, 1);
  const lastDay = new Date(year, monthIdx + 1, 0);
  const startWeekday = firstDay.getDay(); // 0 = domingo
  const daysInMonth = lastDay.getDate();
  // Total de células (preenchidas e vazias) — múltiplo de 7
  const totalCells = Math.ceil((startWeekday + daysInMonth) / 7) * 7;

  // Agrupa aulas por dia do mês corrente
  const aulasByDay = new Map<number, AulaStudentRow[]>();
  for (const a of aulas) {
    const d = new Date(a.dataHora);
    if (d.getFullYear() === year && d.getMonth() === monthIdx) {
      const day = d.getDate();
      if (!aulasByDay.has(day)) aulasByDay.set(day, []);
      aulasByDay.get(day)!.push(a);
    }
  }

  const today = new Date();
  const isCurrentMonth =
    today.getFullYear() === year && today.getMonth() === monthIdx;

  const monthLabel = month.toLocaleDateString("pt-BR", {
    month: "long",
    year: "numeric"
  });
  const weekdayLabels = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  return (
    <section class="rounded-md border border-border bg-card/40 p-4">
      <header class="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2 class="text-base font-bold capitalize">{monthLabel}</h2>
        <div class="flex items-center gap-1">
          <button
            type="button"
            onClick={onPrev}
            title="Mês anterior"
            aria-label="Mês anterior"
            class="grid size-8 place-items-center rounded-md border border-border bg-background hover:bg-accent"
          >
            <ChevronLeft className="size-3.5" />
          </button>
          <button
            type="button"
            onClick={onToday}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
          >
            Hoje
          </button>
          <button
            type="button"
            onClick={onNext}
            title="Próximo mês"
            aria-label="Próximo mês"
            class="grid size-8 place-items-center rounded-md border border-border bg-background hover:bg-accent"
          >
            <ChevronRight className="size-3.5" />
          </button>
        </div>
      </header>

      <div class="grid grid-cols-7 gap-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
        {weekdayLabels.map((w) => (
          <div key={w} class="px-1 py-1 text-center">
            {w}
          </div>
        ))}
      </div>

      <div class="grid grid-cols-7 gap-1">
        {Array.from({ length: totalCells }).map((_, i) => {
          const dayNum = i - startWeekday + 1;
          if (dayNum < 1 || dayNum > daysInMonth) {
            return <div key={i} class="min-h-[2.75rem] rounded-md" />;
          }
          const dayAulas = aulasByDay.get(dayNum) ?? [];
          const isToday = isCurrentMonth && today.getDate() === dayNum;
          return (
            <div
              key={i}
              class={`flex min-h-[2.75rem] flex-col gap-0.5 rounded-md border p-1 ${
                isToday
                  ? "border-primary/60 bg-primary/10"
                  : dayAulas.length > 0
                  ? "border-border bg-background/60"
                  : "border-border/40 bg-background/20"
              }`}
            >
              <div
                class={`flex items-center justify-between gap-1 px-0.5 text-[11px] font-bold tabular-nums leading-none ${
                  isToday ? "text-primary" : "text-foreground/70"
                }`}
              >
                <span>{dayNum}</span>
                {dayAulas.length > 0 && !isToday && (
                  <span class="size-1.5 rounded-full bg-primary/70" aria-hidden />
                )}
              </div>
              <div class="flex min-h-0 flex-1 flex-col gap-[2px] overflow-hidden">
                {dayAulas.slice(0, 2).map((a) => {
                  const hora = new Date(a.dataHora).toLocaleTimeString("pt-BR", {
                    hour: "2-digit",
                    minute: "2-digit"
                  });
                  const tone =
                    a.status === "realized"
                      ? "bg-emerald-500 text-white"
                      : a.status === "canceled"
                      ? "bg-muted text-muted-foreground line-through"
                      : "bg-primary text-primary-foreground";
                  return (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => onOpenAula(a)}
                      class={`flex items-center gap-1 truncate rounded-sm px-1 py-[2px] text-left text-[10px] font-bold leading-tight ${tone} shadow-sm transition-transform hover:scale-[1.02]`}
                      title={`${a.disciplinaName} · ${hora}`}
                    >
                      <span class="tabular-nums opacity-80">{hora}</span>
                      <span class="truncate">{a.disciplinaName}</span>
                    </button>
                  );
                })}
                {dayAulas.length > 2 && (
                  <button
                    type="button"
                    onClick={() => onOpenAula(dayAulas[2])}
                    class="rounded-sm px-1 text-left text-[10px] font-semibold text-muted-foreground hover:bg-accent hover:text-foreground"
                  >
                    +{dayAulas.length - 2} mais
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
